import React from 'react';
import { handleAmount } from '../../../../shared/extra';
import { useDispatch, useSelector } from '../../../../imports/generalImport';
import { Box, Typography, List, ListItem, Divider } from '../../../../imports/muiImport';
import { calculatePayment } from '../customPaymentHook';
import { couponAppliedReset } from '../../../../store/productDetails/action';

function PaymentOrderReceipt({ isWalletAccepted }) {
  const dispatch = useDispatch();

  const { discount, points, isCashbackProduct } = useSelector(
    (state) => state.ProductDetail?.allData || {}
  );

  const DENOMINATION_STORE_DATA = useSelector((state) => state?.Denominations?.allData);

  const COUPON_STORE_DATA = useSelector((state) => state?.Denominations?.couponData);

  const userProfile = useSelector((state) => state.UserProfile.allData?.data);

  const balance = userProfile?.balance || 0;

  /* -------------------- PAYMENT CALCULATION -------------------- */
  const paymentData = React.useMemo(() => {
    const calculationResult = calculatePayment(
      DENOMINATION_STORE_DATA,
      discount,
      isCashbackProduct
    );

    const couponDiscount = () => {
      let couponAmount = 0;
      const netPay = isCashbackProduct ? calculationResult?.totalGrossAmt : calculationResult?.totalGrossAmt - calculationResult?.totalDiscountAmt;

      if (COUPON_STORE_DATA?.isCouponApplied) {
        if (COUPON_STORE_DATA?.discountType === "PERCENTAGE") {
          const dis = (netPay * COUPON_STORE_DATA?.discountValue) / 100;
          couponAmount = dis;

          if (COUPON_STORE_DATA?.maxDiscount > 0 && COUPON_STORE_DATA?.maxDiscount <= dis) {
            couponAmount = COUPON_STORE_DATA?.maxDiscount;
          }
        }

        if (COUPON_STORE_DATA?.discountType === "FLAT") {
          couponAmount = COUPON_STORE_DATA?.discountValue;
        }
      }

      return couponAmount;
    };

    const walletPayAmt = isWalletAccepted ? Math.min(balance, calculationResult.netPaymentAmt) : 0;

    const netPay = isWalletAccepted
      ? Math.max(calculationResult.netPaymentAmt - (walletPayAmt + couponDiscount()), 0)
      : calculationResult.netPaymentAmt - couponDiscount();

    const minAmount = COUPON_STORE_DATA?.conditions?.minPurchaseAmount || 0;
    const maxAmount = COUPON_STORE_DATA?.conditions?.maxPurchaseAmount || Infinity;

    const isCouponValid =
      COUPON_STORE_DATA?.isCouponApplied &&
      calculationResult.totalGrossAmt >= minAmount &&
      calculationResult.totalGrossAmt <= maxAmount;

    return {
      TOTAL_VALUE: calculationResult.totalGrossAmt,
      TOTAL_DISCOUNT: calculationResult.totalDiscountAmt,
      NET_PAY: netPay,
      WALLET_PAY_AMT: walletPayAmt,
      WALLET_BALANCE: balance,
      COUPON_DISCOUNT: couponDiscount(),
      REWARD_POINTS: points || 0,
      IS_SHOW_COUPON_DISCOUNT_COMPONENT: isCouponValid,
    };
  }, [
    DENOMINATION_STORE_DATA,
    discount,
    isCashbackProduct,
    isWalletAccepted,
    balance,
    points,
    COUPON_STORE_DATA,
  ]);

  /* -------------------- COUPON INVALID RESET -------------------- */
  React.useEffect(() => {
    if (COUPON_STORE_DATA?.isCouponApplied && !paymentData.IS_SHOW_COUPON_DISCOUNT_COMPONENT) {
      dispatch(couponAppliedReset());
    }
  }, [COUPON_STORE_DATA?.isCouponApplied, paymentData.IS_SHOW_COUPON_DISCOUNT_COMPONENT, dispatch]);

  /* -------------------- UI -------------------- */
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #0B4AA2 0%, #0A3C84 100%)',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          px: 2,
          py: 1,
        }}
      >
        <Typography
          sx={{
            color: 'white.main',
            fontWeight: 500,
            fontSize: '1.1rem',
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          Order Summary
        </Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          border: '1.5px solid',
          borderColor: 'divider',
          borderTop: 'none',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          backgroundColor: 'white.main',
        }}
      >
        <List sx={{ p: 2 }}>
          {/* Total Value */}
          <ListItem disableGutters sx={{ py: 1 }}>
            <Typography flex={1}>Total Value</Typography>
            <Typography>₹ {handleAmount(paymentData.TOTAL_VALUE.toFixed(2))}</Typography>
          </ListItem>
          <Divider />

          {/* Discount */}
          <ListItem disableGutters sx={{ py: 1 }}>
            <Typography flex={1}>{isCashbackProduct ? 'Cashback' : `Discount (${discount || 0}%)`}</Typography>
            <Typography color="#D32F2F">
              ₹ {handleAmount(paymentData.TOTAL_DISCOUNT.toFixed(2))}
            </Typography>
          </ListItem>
          <Divider />

          {/* Coupon */}
          {paymentData?.IS_SHOW_COUPON_DISCOUNT_COMPONENT && (
            <>
              <ListItem disableGutters sx={{ py: 1 }}>
                <Typography flex={1}>Coupon Applied</Typography>
                <Typography color="#D32F2F">
                  ₹ {handleAmount(paymentData?.COUPON_DISCOUNT?.toFixed(2))}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Wallet */}
          {paymentData?.WALLET_PAY_AMT > 0 && (
            <>
              <ListItem disableGutters sx={{ py: 1 }}>
                <Typography flex={1}>Wallet</Typography>
                <Typography color="#D32F2F">
                  ₹ {handleAmount(paymentData.WALLET_PAY_AMT.toFixed(2))}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Net Pay */}
          <ListItem disableGutters sx={{ py: 1.5 }}>
            <Typography flex={1} fontWeight={500} color="green.main">
              Total Payout
            </Typography>
            <Typography fontWeight={500} color="green.main">
              ₹ {handleAmount(Math.max(paymentData.NET_PAY, 0).toFixed(2))}
            </Typography>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
}

export default PaymentOrderReceipt;
